import { useQueryClient } from '@tanstack/react-query';

// ---------------------------------------------------------------------------
// Query key factories per data domain
// ---------------------------------------------------------------------------

export const queryKeys = {
  blueprint: (slug: string) => ['blueprint', slug] as const,
  inventory: (slug: string) => ['inventory', slug] as const,
  readiness: (slug: string, profileId?: string) => ['readiness', slug, profileId] as const,
  suppliers: (slug: string) => ['suppliers', slug] as const,
  supplierDetail: (slug: string, entryId: string) => ['supplier-detail', slug, entryId] as const,
  connectors: (slug: string) => ['connectors', slug] as const,
  findings: (slug: string) => ['findings', slug] as const,
  remediationDrafts: (slug: string, findingId: string) => ['remediation-drafts', slug, findingId] as const,
  remediationSummary: (slug: string) => ['remediation-summary', slug] as const,
  aiPolicy: (slug: string) => ['ai-policy', slug] as const,
  dataMode: (slug: string) => ['data-mode', slug] as const,
  fieldClasses: (slug: string) => ['field-classes', slug] as const,
  customerAiProviders: (slug: string) => ['customer-ai-providers', slug] as const,
  federalParity: (slug: string) => ['federal-parity', slug] as const,
  evidence: (slug: string) => ['evidence', slug] as const,
  controlChecks: (slug: string) => ['control-checks', slug] as const,
  systems: (slug: string) => ['systems', slug] as const,
  dashboard: (slug: string) => ['dashboard', slug] as const,
  complianceTargets: (slug: string) => ['compliance-targets', slug] as const,
  coverageRegions: (slug: string) => ['coverage-regions', slug] as const,
  coverageImpact: (slug: string) => ['coverage-impact', slug] as const,
  profile: (slug: string) => ['profile', slug] as const,
  /** V112-3-10: the workspace profile (GET /api/w/:slug/profile) and systems list the scope screens read. */
  scopeProfile: (slug: string) => ['scope-profile', slug] as const,
  scopeSystems: (slug: string) => ['scope-systems', slug] as const,
  /** The rules that apply to a system now, with what each depends on (its Scope tab's "Leads to"). */
  scopeLeads: (slug: string, systemId: string) => ['scope-leads', slug, systemId] as const,
  registerItem: (slug: string, itemId: string) => ['register-item', slug, itemId] as const,
  roadmap: (slug: string) => ['roadmap', slug] as const,
  assessment: (profileId: string) => ['assessment', profileId] as const,
  registerAssignees: (slug: string) => ['register-assignees', slug] as const,
  queue: (params: Record<string, unknown>) => ['queue', params] as const,
  queueSummary: () => ['queue-summary'] as const,
  // V112-4-7: under queue-summary so every queue refetch refreshes it.
  controlImpactStats: () => ['queue-summary', 'control-impact-stats'] as const,
  // v10.4 Track G: needs-authoring lane (items) and its count (Today).
  authoringItems: () => ['authoring-items'] as const,
  authoringSummary: () => ['authoring-items', 'summary'] as const,
  orgDashboard: (orgSlug: string) => ['org-dashboard', orgSlug] as const,
  orgFindings: (orgSlug: string) => ['org-findings', orgSlug] as const,
  orgSuppliers: (orgSlug: string) => ['org-suppliers', orgSlug] as const,
  orgAlerts: (orgSlug: string, filters?: Record<string, unknown>) => ['org-alerts', orgSlug, filters] as const,
  orgControlHealth: (orgSlug: string) => ['org-control-health', orgSlug] as const,
  orgJurisdictions: (orgSlug: string) => ['org-jurisdictions', orgSlug] as const,
  orgSettings: (orgSlug: string) => ['org-settings', orgSlug] as const,
  orgAiSystems: (orgSlug: string) => ['org-ai-systems', orgSlug] as const,
  orgIncidents: (orgSlug: string) => ['org-incidents', orgSlug] as const,
  orgPolicy: (orgSlug: string) => ['org-policy', orgSlug] as const,
  alertPacket: (token: string) => ['alert-packet', token] as const,
  alertPreferences: (slug: string) => ['alert-preferences', slug] as const,
  alertDetail: (slug: string, id: string) => ['alert-detail', slug, id] as const,
  incidents: (slug: string) => ['incidents', slug] as const,
  assessorEngagements: (slug: string) => ['assessor-engagements', slug] as const,
  assessorInbox: () => ['assessor-inbox'] as const,
  assessorWorkpaper: (slug: string, engagementId: string) => ['assessor-workpaper', slug, engagementId] as const,
  assessorAnnotations: (slug: string, engagementId: string) => ['assessor-annotations', slug, engagementId] as const,
  qaThreads: (slug: string, engagementId: string) => ['qa-threads', slug, engagementId] as const,
  evidenceRequestsForEngagement: (slug: string, engagementId: string) => ['evidence-requests-engagement', slug, engagementId] as const,
  questionnaires: (slug: string) => ['questionnaires', slug] as const,
  questionnaireDetail: (slug: string, id: string) => ['questionnaire-detail', slug, id] as const,
  myEvidenceRequests: (slug: string, status: string) => ['my-evidence-requests', slug, status] as const,
  apiTokens: (slug: string) => ['api-tokens', slug] as const,
  apiTokenPurposes: (slug: string) => ['api-token-purposes', slug] as const,
  webhookEndpoints: (slug: string) => ['webhook-endpoints', slug] as const,
  signatureRouting: (slug: string, lane: string) => ['signature-routing', slug, lane] as const,
  auditEvents: (slug: string, eventType?: string[]) => ['audit-events', slug, eventType] as const,
  disputes: (slug: string) => ['disputes', slug] as const,
  myWork: (slug: string) => ['my-work', slug] as const,
  myQuestionnaireRollups: (slug: string) => ['my-questionnaire-rollups', slug] as const,
  evidenceGaps: (slug: string) => ['evidence-gaps', slug] as const,
  useCases: (slug: string, status?: string) => ['use-cases', slug, status] as const,
  releases: (slug: string) => ['releases', slug] as const,
  register: (slug: string) => ['register', slug] as const,
  registerItemFull: (slug: string, itemId: string) => ['register-item-full', slug, itemId] as const,
  wsAssessment: (slug: string, profileId: string) => ['ws-assessment', slug, profileId] as const,
  wsProfile: (slug: string, profileId: string) => ['ws-profile', slug, profileId] as const,
  allFrameworks: (slug: string) => ['all-frameworks', slug] as const,
  progress: (slug: string, profileId: string) => ['progress', slug, profileId] as const,
  readinessHistory: (slug: string, profileId: string) => ['readiness-history', slug, profileId] as const,
  githubConnection: (slug: string) => ['github-connection', slug] as const,
  setupSummary: (slug: string) => ['setup-summary', slug] as const,
  systemObligations: (slug: string, systemId: string) => ['system-obligations', slug, systemId] as const,
  commonControls: (slug: string) => ['common-controls', slug] as const,
  allControls: (slug: string) => ['all-controls', slug] as const,
  // V102-8-7 — test library + evidence engagement view
  libraryTests: (slug: string, filter?: string) => ['library-tests', slug, filter] as const,
  libraryTest: (slug: string, testKey: string) => ['library-test', slug, testKey] as const,
  evidenceEngagement: (slug: string, testKey: string | null) => ['evidence-engagement', slug, testKey] as const,
  controlSpecs: (slug: string) => ['control-specs', slug] as const,
  controlSpecOverrides: (slug: string) => ['control-spec-overrides', slug] as const,
  exceptions: (slug: string, filter?: string) => ['exceptions', slug, filter] as const,
  components: (slug: string, systemId: string) => ['components', slug, systemId] as const,
  systemSnapshots: (slug: string, systemId: string) => ['system-snapshots', slug, systemId] as const,
  capabilities: (slug: string, profileId: string) => ['capabilities', slug, profileId] as const,
  sources: (statusFilter?: string, kindFilter?: string) => ['sources', statusFilter, kindFilter] as const,
  supplierRegister: (slug: string, id: string) => ['supplier-register', slug, id] as const,
  supplierCompliance: (slug: string, vendorId: string) => ['supplier-compliance', slug, vendorId] as const,
  supplierDisclosure: (slug: string, entryId: string) => ['supplier-disclosure', slug, entryId] as const,
  supplierDependents: (slug: string, entryId: string) => ['supplier-dependents', slug, entryId] as const,
  discoveredTools: (slug: string, filter?: string) => ['discovered-tools', slug, filter] as const,
  orgSupplierDetail: (orgSlug: string, id: string) => ['org-supplier-detail', orgSlug, id] as const,
  alerts: (slug: string, aggregate?: boolean) => ['alerts', slug, aggregate] as const,
  alertForwards: (slug: string, id: string) => ['alert-forwards', slug, id] as const,
  myWorkspaces: () => ['my-workspaces'] as const,
  outcomes: (slug: string) => ['outcomes', slug] as const,
  controlCoverage: (slug: string) => ['control-coverage', slug] as const,
  systemDetail: (slug: string, id: string) => ['system-detail', slug, id] as const,
  worklist: (slug: string, id: string) => ['worklist', slug, id] as const,
  controlSources: (slug: string, ids: string) => ['control-sources', slug, ids] as const,
  systemDependencies: (slug: string, id: string) => ['system-dependencies', slug, id] as const,
  monitoringBindings: (slug: string, id: string) => ['monitoring-bindings', slug, id] as const,
  ciSubmissions: (slug: string, id: string) => ['ci-submissions', slug, id] as const,
  githubConfig: () => ['github-config'] as const,
  lineage: (slug: string, systemId: string) => ['lineage', slug, systemId] as const,
  gateResults: (slug: string, systemId: string, factsKey?: string) => ['gate-results', slug, systemId, factsKey] as const,
  obligationSummary: (slug: string, systemId: string) => ['obligation-summary', slug, systemId] as const,
  onboardingChecklist: (slug: string) => ['onboarding-checklist', slug] as const,
  buildChecks: (slug: string, systemId: string) => ['build-checks', slug, systemId] as const,
  lifecycleOverview: (slug: string, systemId: string) => ['lifecycle-overview', slug, systemId] as const,
  disclosures: (slug: string) => ['disclosures', slug] as const,
  disclosureVersions: (slug: string, disclosureId: string) => ['disclosure-versions', slug, disclosureId] as const,
  postureConnectors: (slug: string) => ['posture-connectors', slug] as const,
  // V101-1-9 / V101-1-10
  /** Workspace list; with a systemId, the server-filtered list for that system (same prefix). */
  systemAiResources: (slug: string, systemId: string) => ['system-ai-resources', slug, systemId] as const,
  providerSuggestions: (slug: string, systemId?: string) => (systemId ? ['provider-suggestions', slug, systemId] as const : ['provider-suggestions', slug] as const),
  dismissedProviderSuggestions: (slug: string) => ['provider-suggestions-dismissed', slug] as const,
  providerView: (slug: string, providerKey: string) => ['provider-view', slug, providerKey] as const,
  soc2Readiness: (slug: string, optional?: boolean) => ['soc2-readiness', slug, optional] as const,
  auditPeriods: (slug: string) => ['audit-periods', slug] as const,
  certificationProfiles: (slug: string) => ['certification-profiles', slug] as const,
  policyAckStatus: (slug: string) => ['policy-ack-status', slug] as const,
  // V98-3-1 / V98-3-2
  programs: (slug: string) => ['programs', slug] as const,
  today: (slug: string) => ['today', slug] as const,
  meCounts: (slug: string) => ['me-counts', slug] as const,
  programBoundary: (slug: string, program: string, periodId: string | null) => ['program-boundary', slug, program, periodId] as const,
  programExternalServices: (slug: string, program: string) => ['program-external-services', slug, program] as const,
  // V102-8-1 / V102-8-2: CMMC scorecard (per program period)
  programScore: (slug: string, program: string, periodId: string) => ['program-score', slug, program, periodId] as const,
  objectiveReadiness: (slug: string, program: string, periodId: string) => ['objective-readiness', slug, program, periodId] as const,
  programPoam: (slug: string, program: string, periodId: string) => ['program-poam', slug, program, periodId] as const,
  affirmationGate: (slug: string, program: string, periodId: string) => ['affirmation-gate', slug, program, periodId] as const,
  affirmations: (slug: string, program: string) => ['affirmations', slug, program] as const,
  programStale: (slug: string, program: string, periodId: string) => ['program-stale', slug, program, periodId] as const,
  determinations: (slug: string, program: string, periodId: string, state: string) => ['determinations', slug, program, periodId, state] as const,
  programTests: (slug: string, program: string, periodId: string) => ['program-tests', slug, program, periodId] as const,
  // V102-8-5: one cell's evidence options / one proposal's reviewer routing.
  determinationOptions: (slug: string, program: string, periodId: string, objectiveId: string, assetId: string | null) => ['determination-options', slug, program, periodId, objectiveId, assetId ?? 'org'] as const,
  determinationReviewers: (slug: string, program: string, periodId: string, determinationId: string) => ['determination-reviewers', slug, program, periodId, determinationId] as const,
  customerRequirements: (slug: string, program: string, periodId: string) => ['customer-requirements', slug, program, periodId] as const,
  // V103-3-6 / V103-6-3: the GovRAMP overview (program page, Today, FedRAMP Class A) and the Class A readiness items.
  govrampOverview: (slug: string, program: string) => ['govramp-overview', slug, program] as const,
  govrampTierDelta: (slug: string, program: string, to: string) => ['govramp-tier-delta', slug, program, to] as const,
  classA: (slug: string, profileId: string) => ['class-a', slug, profileId] as const,
  alternativeMaterials: (slug: string, profileId: string) => ['alternative-materials', slug, profileId] as const,
};

// ---------------------------------------------------------------------------
// Invalidation taxonomy: mutation type -> downstream query keys to refetch
// ---------------------------------------------------------------------------

/** Keys a program change invalidates (shared by 'program:changed' and 'determination:changed'). */
function programChangedKeys(slug: string) {
  return [
    queryKeys.programs(slug),
    queryKeys.certificationProfiles(slug),
    ['program-boundary', slug] as const,
    ['program-external-services', slug] as const,
    queryKeys.today(slug),
    queryKeys.meCounts(slug),
    // A period moving to fieldwork pins the tests: the score basis and the affirmation gate change.
    ['program-score', slug] as const,
    ['affirmation-gate', slug] as const,
    ['determinations', slug] as const,
    ['program-stale', slug] as const,
    ['customer-requirements', slug] as const,
    // V103-3-6: GovRAMP status, events and the Class A page read the same records.
    ['govramp-overview', slug] as const,
    ['govramp-tier-delta', slug] as const,
    ['class-a', slug] as const,
    ['alternative-materials', slug] as const,
    // V108-8-14 (journey audit): a program added or changed moves Setup's steps and coverage and Today's program
    // cards at once (carry-over from existing sources, the new program's next steps), never after a stale window.
    ['setup-enablers', slug] as const,
    ['setup-coverage', slug] as const,
    ['setup-no-source', slug] as const,
    ['today-programs', slug] as const,
    // V108-8-15: a program's key fields (and what a change does once a period exists).
    ['program-fields', slug] as const,
    ['objective-readiness', slug] as const,
  ];
}

export const invalidationMap = {
  'connector:changed': (slug: string) => [
    queryKeys.postureConnectors(slug),
    ['system-ai-resources', slug] as const,
    queryKeys.providerSuggestions(slug),
    ['provider-view', slug] as const,
    ['soc2-readiness', slug] as const,
    queryKeys.blueprint(slug),
    queryKeys.inventory(slug),
    queryKeys.readiness(slug),
    queryKeys.controlChecks(slug),
    queryKeys.dashboard(slug),
  ],
  'provider-suggestion:changed': (slug: string) => [
    queryKeys.providerSuggestions(slug),
    queryKeys.dismissedProviderSuggestions(slug),
    ['provider-view', slug] as const,
  ],
  'supplier:changed': (slug: string) => [
    queryKeys.suppliers(slug),
    queryKeys.blueprint(slug),
  ],
  'evidence:changed': (slug: string) => [
    queryKeys.evidence(slug),
    queryKeys.readiness(slug),
    queryKeys.dashboard(slug),
  ],
  'finding:changed': (slug: string) => [
    queryKeys.findings(slug),
    queryKeys.readiness(slug),
    queryKeys.dashboard(slug),
  ],
  'system:changed': (slug: string) => [
    queryKeys.systems(slug),
    queryKeys.blueprint(slug),
    queryKeys.readiness(slug),
    queryKeys.dashboard(slug),
  ],
  'control-check:changed': (slug: string) => [
    queryKeys.controlChecks(slug),
    queryKeys.findings(slug),
    queryKeys.readiness(slug),
  ],
  'compliance-target:changed': (slug: string) => [
    queryKeys.complianceTargets(slug),
    queryKeys.dashboard(slug),
  ],
  'profile:changed': (slug: string) => [
    queryKeys.profile(slug),
    queryKeys.scopeProfile(slug),
    queryKeys.scopeSystems(slug),
    ['scope-leads', slug] as const,
    queryKeys.readiness(slug),
    queryKeys.blueprint(slug),
    queryKeys.dashboard(slug),
  ],
  'audit-period:changed': (slug: string) => [
    queryKeys.auditPeriods(slug),
    queryKeys.certificationProfiles(slug),
    ['soc2-readiness', slug] as const,
    queryKeys.programs(slug),
  ],
  'program:changed': (slug: string) => programChangedKeys(slug),
  // V102-8-5: a determination recorded, sent for review, sent back, reviewed or superseded.
  'determination:changed': (slug: string) => [
    ...programChangedKeys(slug),
    ['objective-readiness', slug] as const,
    ['determination-options', slug] as const,
    ['determination-reviewers', slug] as const,
  ],
  'queue:changed': (_slug: string) => [
    queryKeys.queueSummary(),
  ],
  'worklist:changed': (slug: string) => [
    ['worklist', slug] as const,
    queryKeys.controlChecks(slug),
    queryKeys.dashboard(slug),
  ],
  'dependency:changed': (slug: string) => [
    ['system-dependencies', slug] as const,
    queryKeys.systems(slug),
  ],
  'classification:changed': (slug: string) => [
    ['system-detail', slug] as const,
    queryKeys.systems(slug),
    queryKeys.scopeSystems(slug),
    ['scope-leads', slug] as const,
    queryKeys.dashboard(slug),
    // V108-8-19: a save computes obligations, which closes (or keeps) the Classify step and moves coverage.
    ['setup-enablers', slug] as const,
    ['setup-coverage', slug] as const,
    queryKeys.today(slug),
  ],
} as const;

// ---------------------------------------------------------------------------
// useInvalidate — call after a mutation to invalidate downstream caches
// ---------------------------------------------------------------------------

/**
 * Returns a function that, given a mutation type and workspace slug,
 * invalidates every downstream query key so consuming pages refetch.
 *
 * Usage:
 * ```ts
 * const invalidate = useInvalidate();
 * // after saving a connector:
 * invalidate('connector:changed', slug);
 * ```
 */
export function useInvalidate() {
  const queryClient = useQueryClient();
  return (mutation: keyof typeof invalidationMap, slug: string) => {
    const keys = invalidationMap[mutation](slug);
    for (const key of keys) {
      queryClient.invalidateQueries({ queryKey: key });
    }
  };
}
